import {
    EnumDialogName,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { useTracking } from '@rhg/libs/analytics';
import { logger } from '@rhg/libs/logger';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GeneralErrorDialog } from '../general-error-dialog';
import { registrationCountryErrorMessages } from './messages';

/**
 * Which sponsorship path raised the country mismatch. `invitation` is a
 * dependent registration and `upgrade` is not, so the two share neither the
 * reported dialog name nor the body copy, which names an inviter.
 */
export type CountryErrorSource = 'invitation' | 'upgrade';

const configBySource: Record<
    CountryErrorSource,
    {
        trackDialog: EnumDialogName;
        subtitle: typeof registrationCountryErrorMessages.subtitle;
    }
> = {
    invitation: {
        trackDialog: 'DependentRegistrationError',
        subtitle: registrationCountryErrorMessages.subtitle,
    },
    upgrade: {
        trackDialog: 'RegistrationCountryError',
        subtitle: registrationCountryErrorMessages.upgradeSubtitle,
    },
};

export interface CountryErrorDialogProps {
    source?: CountryErrorSource;
    trackScreen?: EnumScreenName;
    trackProps?: EventProperties;
    onClose: () => void;
}

/**
 * Error dialog for a country mismatch between the two sides of a sponsorship.
 * Covers an invitation whose sender and recipient reside in different
 * countries, and an upgrade that rejects an international user against a
 * domestic partner.
 *
 * @param props.source - Sponsorship path that raised the error, defaults to `invitation`
 * @param props.trackScreen - Screen name reported with the dialog's events
 * @param props.trackProps - Additional properties reported with the dialog's events
 * @param props.onClose - Called when the dialog is dismissed
 */
export function CountryErrorDialog({
    source = 'invitation',
    trackScreen,
    trackProps,
    onClose,
}: CountryErrorDialogProps) {
    const { trackDialog, subtitle } = configBySource[source];
    const { formatMessage } = useIntl();
    const { track } = useTracking();
    const trackCountryError = useCallback(() => {
        track('RegistrationCountryError', trackProps).catch((err) =>
            logger.error(
                'Failed to track RegistrationCountryError',
                {},
                err as Error
            )
        );
    }, [track, trackProps]);

    return (
        <GeneralErrorDialog
            onOpen={trackCountryError}
            trackDialog={trackDialog}
            trackCloseButton="Close"
            trackScreen={trackScreen}
            trackProps={trackProps}
            title={formatMessage(registrationCountryErrorMessages.title)}
            errorContent={formatMessage(subtitle)}
            onClose={onClose}
            showSupportEmail
        />
    );
}
